/**
 * @file Post-auth route restoration and browser history coordination.
 */

import { state } from "../state/state.js";
import {
    clearPostAuthDestination,
    isProtectedDestinationPath,
    readPostAuthDestination,
} from "../auth/postAuthDestination.js";
import { navigate } from "./navigationController.js";
import { showSystemToast } from "../../shared/ui/notifications.js";
import {
    isDetailPath,
    parseDetailRouteCandidate,
} from "./detailRoutes.js";
import { restoreDetailRoute } from "./detailRouteCoordinator.js";
import { parseDrugRouteCandidate } from "./drugRoutes.js";
import {
    getCalendarDayDateKey,
    parseCalendarRouteCandidate,
} from "./calendarRoutes.js";
import { isApprovalNotification } from "../notifications/notificationTargets.js";
import {
    canAccessView,
    getViewAccessDenialMessage,
} from "./boardAccess.js";
import {
    getViewKeyFromPath,
    normalizeRouteSubView,
} from "./boardRoutes.js";

const EMAIL_NOTIFICATION_ID_PARAM = "notificationId";

function isMobileCalendarAgendaActive() {
    return state.currentView === "calendar" &&
        Boolean(getCalendarDayDateKey(state.calendarView)) &&
        window.matchMedia("(max-width: 767px)").matches;
}

function notifyDestinationOutcome(outcome) {
    if (!outcome?.message || outcome.toastShown) {return;}
    showSystemToast(outcome.message, { id: `auth-destination-${outcome.reason || "notice"}` });
}

function buildDestinationOutcome(reason, message = "") {
    return { restored: false, reason, message };
}

function getEmailNotificationId(search = window.location.search) {
    const params = new URLSearchParams(search || "");
    return params.get(EMAIL_NOTIFICATION_ID_PARAM)?.trim() || null;
}

function removeEmailNotificationIdFromCurrentUrl() {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(EMAIL_NOTIFICATION_ID_PARAM)) {return;}

    currentUrl.searchParams.delete(EMAIL_NOTIFICATION_ID_PARAM);
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
}

async function clearEmailNotificationIfAllowed(notificationId) {
    const {
        doc,
        getDoc,
    } = await import("https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js");
    const { db } = await import("../firebase/firebase.js");

    const snapshot = await getDoc(doc(db, "notifications", notificationId));
    if (!snapshot.exists()) {return;}

    const notification = snapshot.data();
    if (isApprovalNotification(notification.triggerEvent)) {
        return;
    }

    await openRestoredEmailNotificationTarget(notification);

    if (notification.read !== false) {
        return;
    }

    const {
        markSingleNotificationRead,
    } = await import("../notifications/notificationApi.js");
    await markSingleNotificationRead(notificationId);
}

async function openRestoredEmailNotificationTarget(notification = {}) {
    if (
        notification.parentType === "ordering" &&
        notification.parentId &&
        state.currentView === "ordering"
    ) {
        const { openOrderingNotificationItem } =
            await import("../../domains/ordering/orderingModal.js");
        await new Promise((resolve) => window.requestAnimationFrame(resolve));
        if (state.currentView !== "ordering") {return;}
        await openOrderingNotificationItem(notification.parentId);
        return;
    }
    if (
        notification.parentType !== "recalls" ||
        notification.contextType !== "note" ||
        !notification.parentId ||
        state.currentView !== "recalls"
    ) {
        return;
    }

    const { openRecallNotesHistoryModal } =
        await import("../../domains/recalls/recallsNotesModal.js");
    await openRecallNotesHistoryModal(notification.parentId);
}

async function processEmailNotificationRead(notificationId, routeOutcome) {
    if (!notificationId) {return;}

    try {
        if (routeOutcome?.restored) {
            await clearEmailNotificationIfAllowed(notificationId);
        }
    } catch (error) {
        console.warn("Email notification read processing skipped:", error);
    } finally {
        removeEmailNotificationIdFromCurrentUrl();
    }
}

function isTaskDetailDestination(destination) {
    return destination?.view === "details" && destination?.itemType === "task";
}

function getDeniedDestinationMessage(destination) {
    if (isTaskDetailDestination(destination)) {
        return "You signed in but you do not have permission to access this item or it has been deleted.";
    }
    if (destination?.view === "details") {
        return "You signed in, but that detail page is not available with your current access.";
    }
    if (destination?.view === "ordering") {
        return getViewAccessDenialMessage(state.currentUserProfile, "ordering");
    }
    if (destination?.view === "compliance") {
        return getViewAccessDenialMessage(state.currentUserProfile, "compliance");
    }
    return "You signed in, but that destination is not available with your current access.";
}

async function restoreDestination(destination, options = {}) {
    const {
        view,
        itemId,
        itemType,
        routeStatus,
        subView,
    } = destination || {};
    const historyMode = options.historyMode || "push";
    if (view === "details") {
        if (routeStatus === "invalid") {
            navigate("tasks", null, { historyMode });
            return buildDestinationOutcome(
                "destination_invalid",
                "This detail link is invalid.",
            );
        }

        const routeTarget = destination.pathname ?
            `${destination.pathname}${destination.search || ""}` : {
            itemId,
            itemType,
        };
        const detailOutcome = await restoreDetailRoute(routeTarget, {
            source: options.source || "post_auth",
            historyMode,
            notify: options.notify,
        });
        return detailOutcome.restored
            ? { restored: true, reason: "restored" }
            : {
                restored: false,
                reason: `destination_${detailOutcome.status}`,
                message: detailOutcome.message,
                toastShown: detailOutcome.toastShown === true,
            };
    }

    if (view === "drugs" && routeStatus === "invalid") {
        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome(
            "destination_invalid",
            "This drugs link is invalid.",
        );
    }

    if (view === "calendar" && routeStatus === "invalid") {
        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome(
            "destination_invalid",
            "This calendar link is invalid.",
        );
    }

    if (view && canAccessView(state.currentUserProfile, view)) {
        navigate(view, subView || null, { historyMode });
        return { restored: true, reason: "restored" };
    }

    if (view) {
        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome("destination_denied", getDeniedDestinationMessage(destination));
    }

    navigate("tasks", null, { historyMode });
    return { restored: true, reason: "default_entry" };
}

async function routeFromPath(pathname, options = {}) {
    const historyMode = options.historyMode || "replace";
    const shouldNotify = options.notify !== false;

    if (!pathname || pathname === "/") {
        navigate("tasks", null, { historyMode });
        return {
            restored: true,
            reason: "default_entry",
            toastShown: shouldNotify,
        };
    }

    const detailRouteCandidate = parseDetailRouteCandidate(
        `${pathname}${window.location.search || ""}`,
    );
    if (detailRouteCandidate) {
        return restoreDetailRoute(`${pathname}${window.location.search || ""}`, {
            source: options.source || "startup",
            historyMode,
            notify: shouldNotify,
        });
    }

    const drugRouteCandidate = parseDrugRouteCandidate(pathname);
    if (drugRouteCandidate?.status === "valid") {
        if (canAccessView(state.currentUserProfile, "drugs")) {
            navigate("drugs", drugRouteCandidate.target.subView, { historyMode });
            return { restored: true, reason: "restored" };
        }

        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome("destination_denied", getDeniedDestinationMessage({
            view: "drugs",
        }));
    }

    if (drugRouteCandidate?.status === "invalid") {
        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome("destination_invalid", "This drugs link is invalid.");
    }

    const calendarRouteCandidate = parseCalendarRouteCandidate(pathname);
    if (calendarRouteCandidate?.status === "valid") {
        if (canAccessView(state.currentUserProfile, "calendar")) {
            navigate("calendar", calendarRouteCandidate.target.subView, { historyMode });
            return { restored: true, reason: "restored" };
        }

        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome("destination_denied", getDeniedDestinationMessage({
            view: "calendar",
        }));
    }

    if (calendarRouteCandidate?.status === "invalid") {
        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome("destination_invalid", "This calendar link is invalid.");
    }

    const view = getViewKeyFromPath(pathname);
    if (view && canAccessView(state.currentUserProfile, view)) {
        navigate(view, null, { historyMode });
        return { restored: true, reason: "restored" };
    }

    if (isProtectedDestinationPath(pathname)) {
        navigate("tasks", null, { historyMode });
        return buildDestinationOutcome("destination_denied", getDeniedDestinationMessage({
            view: isDetailPath(pathname) ? "details" : view,
        }));
    }

    navigate("tasks", null, { historyMode });
    return buildDestinationOutcome("destination_missing", "The requested destination is unavailable.");
}

/**
 * Restores a captured destination or current URL after auth bootstrap.
 * @returns {Promise<object>} Route restoration outcome.
 */
export async function restorePostAuthDestination() {
    const destination = readPostAuthDestination();
    if (!destination) {
        const notificationId = getEmailNotificationId(window.location.search);
        const fallbackOutcome = await routeFromPath(window.location.pathname);
        await processEmailNotificationRead(notificationId, fallbackOutcome);
        notifyDestinationOutcome(fallbackOutcome);
        return fallbackOutcome;
    }

    const notificationId = getEmailNotificationId(destination.search);
    const restoreOutcome = await restoreDestination(destination, {
        source: "post_auth",
        historyMode: "replace",
        notify: false,
    });
    await processEmailNotificationRead(notificationId, restoreOutcome);
    if (restoreOutcome?.restored) {
        clearPostAuthDestination();
        return restoreOutcome;
    }

    clearPostAuthDestination();
    notifyDestinationOutcome(restoreOutcome);
    return restoreOutcome;
}

/**
 * Registers browser back/forward handling for app navigation.
 */
export function registerAuthPopstateHandler() {
    window.addEventListener("popstate", async (event) => {
        if (!state.currentUserProfile) {
            return;
        }

        if (isMobileCalendarAgendaActive()) {
            navigate("calendar", null, { historyMode: "replace" });
            return;
        }

        if (event.state) {
            if (event.state.view === "details" && event.state.itemId) {
                if (state.selectedItemId !== event.state.itemId ||
                    state.selectedItemType !== (event.state.itemType || null)) {
                    await restoreDetailRoute({
                        itemId: event.state.itemId,
                        itemType: event.state.itemType || null,
                    }, {
                        source: "popstate",
                        historyMode: "none",
                        notify: false,
                    });
                }
            } else if (event.state.view) {
                const nextSubView = normalizeRouteSubView(
                    event.state.view,
                    event.state.subView || null,
                );
                if (state.currentView !== event.state.view ||
                    state.selectedItemId !== null ||
                    (event.state.view === "drugs" &&
                        state.drugsView !== nextSubView) ||
                    (event.state.view === "calendar" &&
                        state.calendarView !== nextSubView)) {
                    navigate(event.state.view, nextSubView, { historyMode: "none" });
                }
            } else {
                navigate("tasks", null, { historyMode: "none" });
            }
        } else {
            await routeFromPath(window.location.pathname, {
                source: "popstate",
                historyMode: "none",
                notify: false,
            });
        }
    });
}
